import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import classNames from 'classnames';
import { useAuth } from '../../context/AuthContext';
import DoctorChangePasswordDialog from '../../components/DoctorChangePasswordDialog';
import { CheckCircle2, AlertTriangle, Link as LinkIcon } from 'lucide-react';

/** ==== 型定義（最低限の防御） ==== */
type DoctorMe = {
  id?: number;
  email?: string;
  name?: string | null;                 // ← サーバが返す医師表示名
  zoom_email?: string | null;
  zoom_verified?: boolean | null;       // 無い場合は null 扱い
  profile?: {
    full_name?: string | null;
  } | null;
};

const DoctorMyPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { auth } = useAuth();

  // パスワード変更モーダル
  const [showPwdDlg, setShowPwdDlg] = useState(false);

  // /doctor/me 情報
  const [me, setMe] = useState<DoctorMe | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 氏名編集
  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // トークン（レイアウトと同等のフォールバック）
  const storageToken = useMemo(() => localStorage.getItem('token') || '', [auth.token]);
  const effectiveToken = auth.token || storageToken;

  /** ==== /doctor/me 取得 ==== */
  useEffect(() => {
    const run = async () => {
      if (!effectiveToken) {
        setMe(null);
        return;
      }
      try {
        setLoading(true);
        setErr(null);
        const res = await axios.get('/doctor/me', {
          headers: { Authorization: `Bearer ${effectiveToken}` },
        });
        setMe(res.data || null);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || e?.message || '読み込みに失敗しました');
        setMe(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [effectiveToken]);

  /** ==== 表示用ヘルパ ==== */
  // 表示名は name > profile.full_name > email の優先順
  const displayName =
    (me?.name ?? '')?.trim() ||
    (me?.profile?.full_name ?? '')?.trim() ||
    me?.email ||
    (i18n.language === 'ja' ? '名称未設定' : 'No name');

  // Zoom 連携状態（zoom_email と zoom_verified を併用判定）
  const zoomState: 'linked' | 'unlinked' | 'need_verify' = (() => {
    if (!me?.zoom_email) return 'unlinked';
    if (me?.zoom_verified === false || me?.zoom_verified == null) return 'need_verify';
    return 'linked';
  })();

  const zoomBadge = {
    linked: {
      text: t('doctor.zoom_linked') || '連携済み',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: <CheckCircle2 className="w-4 h-4" />,
    },
    need_verify: {
      text: t('doctor.zoom_need_verify') || '要再認証',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: <AlertTriangle className="w-4 h-4" />,
    },
    unlinked: {
      text: t('doctor.zoom_unlinked') || '未連携',
      className: 'bg-gray-50 text-gray-700 border-gray-200',
      icon: <LinkIcon className="w-4 h-4" />,
    },
  }[zoomState];

  /** ==== 氏名編集ハンドラ ==== */
  const startEdit = () => {
    setNameInput((me?.name ?? '') || (me?.profile?.full_name ?? '') || '');
    setEditing(true);
    setSaveMsg(null);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveMsg(null);
  };

  const saveName = async () => {
    const next = nameInput.trim();
    if (!next) {
      setSaveMsg(t('common.input_required') || '必須項目です');
      return;
    }
    try {
      setSaving(true);
      setSaveMsg(null);
      // <重要> バックエンド側で PATCH /doctor/me { name } を受けるようにしてください
      const resp = await axios.patch(
        '/doctor/me',
        { name: next },
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      // 成功時：ローカル状態を更新
      setMe((prev) => (prev ? { ...prev, name: next } : prev));
      setEditing(false);
      setSaveMsg(t('common.saved') || '保存しました');
      // 一応サーバ戻りを優先する場合は resp.data.name を採用
      if (resp?.data?.name) {
        setMe((prev) => (prev ? { ...prev, name: resp.data.name } : prev));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || '保存に失敗しました';
      setSaveMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  /** ==== Zoom クライアント起動 ==== */
  const openZoomApp = () => {
    // 代表的な起動 URI（OS/Zoom クライアントにより差異あり）
    // うまくいかない環境向けにドキュメントリンクなどに差し替え可
    window.location.href = 'zoommtg://zoom.us/launch';
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* タイトル行（右にパスワード変更） */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">
          {t('doctor.tab_mypage') || 'マイページ'}
        </h2>

        <button
          onClick={() => setShowPwdDlg(true)}
          className="text-sm px-4 py-2 rounded-full border hover:bg-gray-100"
        >
          {t('password.change')}
        </button>
      </div>

      {/* 本体カード */}
      <div className="bg-white rounded-2xl shadow p-6 space-y-6">
        {/* 読込/エラー */}
        {loading && <p className="text-sm text-gray-500">{t('common.loading') || '読み込み中…'}</p>}
        {err && <p className="text-sm text-red-600">{err}</p>}

        {!loading && !err && (
          <>
            {/* 氏名/ID */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1">
                <p className="text-gray-500 text-sm">{t('doctor.name') || '氏名'}</p>

                {/* 編集モード／表示モード切替 */}
                {!editing ? (
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-semibold text-gray-900">{displayName}</p>
                    <button
                      onClick={startEdit}
                      className="text-xs px-2 py-1 rounded-lg border hover:bg-gray-100"
                    >
                      {t('common.edit', '編集')}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <input
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm w-full sm:w-72"
                      placeholder={t('doctor.name') || '氏名'}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={saveName}
                        disabled={saving}
                        className={classNames(
                          'text-sm px-3 py-2 rounded-lg text-white',
                          saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-coral hover:opacity-90'
                        )}
                      >
                        {saving ? (t('common.saving') || '保存中…') : (t('common.save') || '保存')}
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={saving}
                        className="text-sm px-3 py-2 rounded-lg border hover:bg-gray-100"
                      >
                        {t('common.cancel') || 'キャンセル'}
                      </button>
                    </div>
                  </div>
                )}

                {saveMsg && <p className="text-xs mt-1 text-gray-600">{saveMsg}</p>}
              </div>

              <div className="text-right">
                <p className="text-gray-500 text-sm">ID</p>
                <p className="text-lg font-semibold text-gray-900">{me?.id ?? '-'}</p>
              </div>
            </div>

            {/* メール/Zoom */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-sm">{t('doctor.login_email') || 'ログインメール'}</p>
                <p className="font-medium text-gray-800 break-all">{me?.email || '-'}</p>
              </div>

              <div>
                <p className="text-gray-500 text-sm">Zoom</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={classNames(
                      'inline-flex items-center gap-1 text-xs font-semibold border px-2 py-0.5 rounded-full',
                      zoomBadge.className
                    )}
                  >
                    {zoomBadge.icon}
                    {zoomBadge.text}
                  </span>
                  <span className="text-gray-800 text-sm">
                    {me?.zoom_email || (t('doctor.no_zoom_email') || '未設定')}
                  </span>
                </div>

                {/* Zoom クライアント起動ボタン（暫定） */}
                <div className="mt-2">
                  <button
                    onClick={openZoomApp}
                    className="text-sm px-3 py-1.5 rounded-lg bg-coral text-white hover:opacity-90"
                  >
                    {t('doctor.open_zoom_app', 'Zoomを開く')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* パスワード変更モーダル */}
      <DoctorChangePasswordDialog
        open={showPwdDlg}
        onClose={() => setShowPwdDlg(false)}
        onSuccess={() => setShowPwdDlg(false)}
      />
    </div>
  );
};

export default DoctorMyPage;
